// The two checkboxes of the Companies page: "only with annual reports" and
// "hide closed". The state is in the URL (?reports=1&open=1).
//
// Each checkbox is an AND of `<>`, because the README forbids IN (...) and an OR.
// No index serves the filter, so kinedb walks the sort index (or scans) and tests
// each row. Most companies match, so a page is fast (cvr.kinedb.com, 2026-09-17):
// sidstOpdateret DESC 0.003 s, stiftelsesDato DESC 0.10 s, no sort 0.004 s.

import { and, ident, quote } from './sql.js';

// The forms without an annual report: NO_FINANCIAL_FORMS of the agent
// (lib/transform.js), and K/S (the owner, 2026-09-17).
export const NO_REPORT_FORMS = ['ENK', 'FOR', 'KOM', 'ØVR', 'FAS', 'STA', 'UOP', 'PAR', 'FKI', 'UDL', 'ANS', 'DØD', 'PMV', 'I/S', 'FON', 'FFO', 'EUO', 'K/S'];

// The dissolved statuses of lib/transform.js. A company UNDER konkurs or UNDER
// frivillig likvidation is not closed (the owner, 2026-09-17).
export const CLOSED_STATUSES = [
  'OPHØRT', 'OPLØSTEFTERFUSION', 'TVANGSOPLØST', 'OPLØSTEFTERERKLÆRING', 'SLETTET',
  'OPLØSTEFTERFRIVILLIGLIKVIDATION', 'OPLØSTEFTERKONKURS', 'OPLØSTEFTERSPALTNING',
  'OPLØSTEFTERGRÆNSEOVERSKRIDENDEHJEMSTEDSFLYTNING', 'OPLØST', 'OPLØSTEFTERGRÆNSEOVERSKRIDENDEFUSION',
];

// Offset 1000 took 1.0-1.3 s, offset 2500 2.1 s, offset 9984 9.6 s.
export const MAX_FILTER_OFFSET = 1000;

// Without a name search, only these sorts are fast. sidstOpdateret ASC took
// 27.3 s, cvrstatus ASC 16.0 s, ophoersDato DESC more than 40 s.
const SERVED_SORTS = new Set(['sidstOpdateret|desc', 'stiftelsesDato|desc']);

function notEqual(field, values) {
  return and(...values.map((value) => `${ident(field, 'field')} <> ${quote(value)}`));
}

/** The SQL filter of the checkboxes, or '' when no checkbox is on. */
export function filterWhere({ reports = false, open = false } = {}) {
  return and(
    reports ? notEqual('virksomhedsformkort', NO_REPORT_FORMS) : '',
    open ? notEqual('cvrstatus', CLOSED_STATUSES) : '',
  );
}

/** The checkboxes in the URL. */
export function readFilters(params) {
  return { reports: params.get('reports') === '1', open: params.get('open') === '1' };
}

/** True when a filtered list without a name search can use this sort. */
export function filterSortServed(order) {
  if (!order?.field) return true;
  return SERVED_SORTS.has(`${order.field}|${order.desc ? 'desc' : 'asc'}`);
}

/**
 * The WHERE and the sort of a list page with the checkboxes.
 * `trusted`: the only filter is the checkbox filter, so the page may send it without
 * an index (the measurements above). A search or a JSON query keeps the plan checks
 * of db.js, because a rare value can walk all 2.2 million documents.
 */
export function filterQuery({ where = '', filters = {}, order, offset = 0 }) {
  const extra = filterWhere(filters);
  if (!extra) return { where, order, filtered: false, trusted: false };
  if (where) return { where: and(where, extra), order, filtered: true, trusted: false };
  if (offset > MAX_FILTER_OFFSET) {
    throw new Error(`With a checkbox on, the site does not page past row ${MAX_FILTER_OFFSET}. Search, or change the sort.`);
  }
  return { where: extra, order: filterSortServed(order) ? order : undefined, filtered: true, trusted: true };
}
